import React, { ReactNode, Ref, useMemo } from 'react';
import { FieldError } from 'react-hook-form';

import cn from 'classnames';

import styles from './Input.module.css';

type InputProps = {
  onChange?: (v: any) => any;
  onChangeText?: (v: any) => any;
  onBlur?: (v: any) => any;
  onFocus?: (v: any) => any;
  onTextBlur?: () => void;
  editable?: boolean;
  error?: FieldError;
  placeholder?: string;
  value?: string;
  type?: string;
  icon?: ReactNode;
  onIconClick?: () => void;
  className?: string;
  labelClassname?: string;
  loading?: boolean;
  focusOnChevronClick?: boolean;
  maskPlaceholder?: string;
} & JSX.IntrinsicElements['input'];

const Input = React.forwardRef(
  (
    {
      onChange,
      onChangeText,
      onBlur,
      onFocus,
      onIconClick,
      error,
      placeholder,
      value = '',
      type,
      icon,
      className,
      labelClassname,
      loading,
      defaultValue,
      maskPlaceholder,
      focusOnChevronClick,
      autoComplete = 'on',
      ...restOfProps
    }: InputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const maskPlaceholderText = useMemo(() => {
      const postfix: string[] = [];
      const prefix = maskPlaceholder?.split('').map((char, i) => {
        const charFromValue = value?.split('')[i];
        if (!charFromValue) {
          postfix.push(char);
        } else {
          return charFromValue;
        }
      });

      return [prefix?.join(''), postfix?.join('')];
    }, [value, maskPlaceholder]);

    const onInputFocus = (e: any) => {
      !!onFocus && onFocus(e);
    };

    const onInputBlur = (e: any) => {
      !!onBlur && onBlur(e);
    };

    return (
      <div className={styles.inputWrapper}>
        <input
          className={cn(
            styles.input,
            focusOnChevronClick && styles.focusOnChevronClick,
            className,
            Boolean(error) && styles.error,
            restOfProps.disabled && styles.input__disabled,
            !!icon && styles.input__secureText,
          )}
          value={value}
          type={type}
          onChange={onChangeText}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          ref={ref}
          data-loading={loading}
          autoComplete={autoComplete}
          {...restOfProps}
        />
        {maskPlaceholder && (
          <span className={styles.maskPlaceholderContainer}>
            <span className={cn(styles.maskPlaceholderText, styles.hide)}>
              {maskPlaceholderText[0]}
            </span>
            <span className={styles.maskPlaceholderText}>{maskPlaceholderText[1]}</span>
          </span>
        )}
        <span className={cn(styles.floatingLabel, labelClassname)}>{placeholder}</span>
        <span className={styles.icon}>{icon}</span>
        {Boolean(error) && <span className={styles.errorLabel}>{error?.message}</span>}
      </div>
    );
  },
);

export default Input;
